import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { Message } from '@repo/shared/src/services/messaging-service/messaging-service.types';
import * as fromActions from './actions';

export interface MessageState extends EntityState<Message> {
  // additional entities state properties
  getMessageError: any | null;
  postMessageError: any | null;
  idMessagesLoaded: string | null;
  isLoaded: boolean;
  isMessageActionPending: boolean;
  isMessageActionNoDraftPending: boolean;
  isResetTextAreaNeeded: boolean;
}

export const adapter: EntityAdapter<Message> = createEntityAdapter<Message>();

export const initialState: MessageState = adapter.getInitialState({
  // additional entity state properties
  getMessageError: null,
  postMessageError: null,
  idMessagesLoaded: null,
  isLoaded: false,
  isMessageActionPending: false,
  isMessageActionNoDraftPending: false,
  isResetTextAreaNeeded: false,
});

export const reducer = createReducer(
  initialState,
  on(fromActions.setMessageList, (state, { messages }) => adapter.setAll(messages, state)),
  on(fromActions.addMessageToList, (state, { message }) => adapter.addOne(message, state)),
  on(fromActions.resetMessageError, state => ({ ...state, getMessageError: null, postMessageError: null })),

  // GET
  on(fromActions.getMessageList, (state, { isLoadingNeeded, conversationId }) => ({
    ...state,
    idMessagesLoaded: isLoadingNeeded ? null : conversationId,
    isLoaded: false,
    error: null,
  })),
  on(fromActions.getMessageListSuccess, (state, { messageList, conversationId }) =>
    adapter.setAll(messageList, { ...state, idMessagesLoaded: conversationId, isLoaded: true }),
  ),
  on(fromActions.getMessageListFailed, (state, { error, conversationId, isLoadingNeeded }) => ({
    ...state,
    getMessageError: isLoadingNeeded ? error : state.getMessageError,
    idMessagesLoaded: conversationId,
    isLoaded: isLoadingNeeded ? false : state.isLoaded,
  })),

  //POST
  on(fromActions.postMessage, (state, { isPostFromDraft }) => ({
    ...state,
    isMessageActionPending: true,
    isMessageActionNoDraftPending: isPostFromDraft ? false : true,
    isResetTextAreaNeeded: false,
    error: null,
  })),
  on(fromActions.postMessageSuccess, (state, { message, isPostFromDraft, conversationChanged }) => {
    if (conversationChanged) {
      return {
        ...state,
        isMessageActionPending: false,
        isMessageActionNoDraftPending: false,
        isResetTextAreaNeeded: isPostFromDraft ? false : true,
      };
    } else {
      return adapter.addOne(message, {
        ...state,
        isMessageActionPending: false,
        isMessageActionNoDraftPending: false,
        isResetTextAreaNeeded: isPostFromDraft ? false : true,
      });
    }
  }),
  on(fromActions.postMessageFailed, (state, { error }) => ({
    ...state,
    postMessageError: error,
    isMessageActionPending: false,
    isMessageActionNoDraftPending: false,
    isResetTextAreaNeeded: false,
  })),

  // PATCH
  on(fromActions.patchMessage, (state, { isPatchFromDraft }) => ({
    ...state,
    isLoaded: true,
    error: null,
    isMessageActionPending: isPatchFromDraft ? false : true,
    isMessageActionNoDraftPending: isPatchFromDraft ? false : true,
    isResetTextAreaNeeded: false,
  })),
  on(fromActions.patchMessageSuccess, (state, { message, isPatchFromDraft }) =>
    adapter.updateOne(message, {
      ...state,
      isLoaded: true,
      isMessageActionPending: false,
      isMessageActionNoDraftPending: false,
      isResetTextAreaNeeded: isPatchFromDraft ? false : true,
    }),
  ),
  on(fromActions.patchMessageFailed, (state, { error }) => ({
    ...state,
    postMessageError: error,
    isLoaded: false,
    isMessageActionPending: false,
    isMessageActionNoDraftPending: false,
    isResetTextAreaNeeded: false,
  })),

  // DELETE
  on(fromActions.deleteMessage, state => ({ ...state, error: null })),
  on(fromActions.deleteMessageSuccess, (state, { messageId }) => adapter.removeOne(messageId, state)),

  // Other
  on(fromActions.unsetIdMessagesLoaded, state => ({ ...state, idMessagesLoaded: null })),
  on(fromActions.unsetListMessage, state => adapter.removeAll(state)),
);

export const getMessageError = (state: MessageState) => state.getMessageError;
export const postMessageError = (state: MessageState) => state.postMessageError;

const { selectIds, selectEntities, selectAll, selectTotal } = adapter.getSelectors();

// select the array of messages ids
export const selectMessagesIds = selectIds;

// select the dictionary of message entities
export const selectMessageEntities = selectEntities;

// select the array of messages
export const selectAllMessages = selectAll;

// select the total messages count
export const selectMessageTotal = selectTotal;
